import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button, Typography, Box, CircularProgress, Alert, Fade, AlertTitle } from "@mui/material";
import { io } from "socket.io-client";
import ServerDown from "./ServerDown";
import { SOCKET_PATH } from "../config/serverConfig";
import { FullRectangleLogo } from "../assets/images/logos";

const PairDevice = ({ ipAddress, socketOrigin, onConfigurationComplete }) => {
  const [pairing, setPairing] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("WAITING");
  console.log("PairDevice status:", status);
  const [configData, setConfigData] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  // Seconds remaining until the current code expires — drives the countdown
  // shown next to the code (parity with the Android app, which always shows a
  // "time remaining" for each generated code).
  const [secondsLeft, setSecondsLeft] = useState(null);

  // Single source of truth for the active status poller so that regeneration
  // and unmount both clean up the same interval (prevents leaked/duplicate
  // pollers — finding #6). Also guards against starting pairing twice.
  const pollIntervalRef = useRef(null);
  const hasStartedRef = useRef(false);

  const getOrCreateDeviceId = () => {
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      localStorage.setItem("deviceId", deviceId);
    }
    return deviceId;
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    // Only start once for the lifetime of the component. `pairing`/`status`
    // are intentionally not dependencies; a ref guard avoids the stale-closure
    // double-start bug (#7).
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const deviceId = getOrCreateDeviceId();

    axios
      .post(`${ipAddress}/pairing/start`, { deviceId })
      .then((res) => {
        setPairing(res.data);
        setIsExpired(false);
        setStatus("WAITING");
        startPolling(res.data.pairingId);
      })
      .catch(() => {
        setError("Failed to start pairing");
      });

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipAddress]);

  // Live countdown for the active code. Recomputes from the server-provided
  // `expiresAt` every second so the displayed timer matches the real expiry
  // even across regeneration. Resets whenever a new pairing session arrives.
  useEffect(() => {
    if (!pairing?.expiresAt) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.round((pairing.expiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [pairing?.expiresAt]);

  // Format seconds as M:SS for display.
  const formatCountdown = (secs) => {
    if (secs == null) return "";
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const startPolling = (pairingId) => {
    // Ensure only one poller is ever active (finding #6).
    stopPolling();

    const deviceId = localStorage.getItem("deviceId");

    // A just-created session can momentarily 404 before the write propagates
    // across cluster workers / the shared Redis store. Discarding the session
    // on the first 404 makes the display regenerate a NEW pairingId while the
    // admin may already be configuring the OLD one on their phone — the phone
    // completes /configure against a session the display abandoned, and the
    // display polls the new session forever (Break 3). Tolerate a few
    // consecutive 404s before treating the session as truly gone. An explicit
    // 410 / status:"EXPIRED" is authoritative and regenerates immediately.
    let consecutive404s = 0;
    const MAX_TRANSIENT_404S = 3; // ~6s at a 2s poll interval

    const handleExpiryOrMissing = () => {
      stopPolling();
      setIsExpired(true);
      setStatus("REGENERATING");
      setTimeout(() => {
        regeneratePairing();
      }, 1000);
    };

    pollIntervalRef.current = setInterval(() => {
      axios
        .get(`${ipAddress}/pairing/status/${pairingId}`, {
          // Sent so the server only releases the device token to the device
          // that started this session (see server finding #10).
          params: deviceId ? { deviceId } : undefined,
        })
        .then((res) => {
          // A successful response proves the session exists — clear any
          // transient-404 streak so brief blips never accumulate to expiry.
          consecutive404s = 0;

          if (res.data.status === "COMPLETED" && res.data.configData) {
            stopPolling();
            setStatus("COMPLETED");
            setConfigData(res.data.configData);

            // Store device token if returned from pairing
            if (res.data.deviceToken) {
              localStorage.setItem("deviceToken", res.data.deviceToken);
            }

            setTimeout(() => {
              handleDeviceConfiguration({
                ...res.data.configData,
                deviceToken: res.data.deviceToken,
              });
            }, 2000);
          } else if (res.data.status === "EXPIRED") {
            handleExpiryOrMissing();
          } else {
            setStatus(res.data.status);
          }
        })
        .catch((err) => {
          const httpStatus = err?.response?.status;

          // 410 Gone is authoritative: the server has explicitly expired the
          // session. Regenerate right away (finding #8 / #9).
          if (httpStatus === 410) {
            handleExpiryOrMissing();
            return;
          }

          // 404 may be transient right after /start (propagation lag) OR a
          // genuinely evicted session. Only regenerate after several
          // consecutive 404s so we don't abandon a session the phone is about
          // to configure (Break 3).
          if (httpStatus === 404) {
            consecutive404s += 1;
            if (consecutive404s >= MAX_TRANSIENT_404S) {
              handleExpiryOrMissing();
            }
            return;
          }

          // Network / 5xx errors: keep polling, don't discard the session.
          console.error("Polling error:", err);
        });
    }, 2000); // Poll every 2 seconds
  };

  const regeneratePairing = () => {
    setIsExpired(false);
    setPairing(null);

    const deviceId = getOrCreateDeviceId();

    axios
      .post(`${ipAddress}/pairing/start`, { deviceId })
      .then((res) => {
        setPairing(res.data);
        setStatus("WAITING");
        // Reuse the managed poller so the interval is tracked and cleaned up
        // (fixes the leaked-poller bug — finding #6).
        startPolling(res.data.pairingId);
      })
      .catch(() => setError("Failed to restart pairing"));
  };

  const handleDeviceConfiguration = async (data) => {
    try {
      setConfigData(data);

      const socket = io(socketOrigin || ipAddress, {
        path: SOCKET_PATH,
        forceNew: true,
        reconnection: false,
        timeout: 5000,
      });

      const deviceNameSafe = (data?.deviceName || "").trim();
      if (!deviceNameSafe) {
        setStatus("ERROR");
        setError("Configuration missing device name");
        socket.disconnect();
        return;
      }

      socket.on("connect", () => {
        socket.emit("checkDevice", deviceNameSafe, (isDeviceExists) => {
          if (isDeviceExists) {
            setStatus("ERROR");
            setError("Device Name already exists!");
            socket.disconnect();
          } else {
            localStorage.setItem("locationMarker", deviceNameSafe);
            localStorage.setItem("terminal", data.terminalId);
            localStorage.setItem("zone", data.zoneId);
            // Store device token for authenticated socket connections
            if (data.deviceToken) {
              localStorage.setItem("deviceToken", data.deviceToken);
            }

            setIsReloading(true);
            setTimeout(() => {
              socket.disconnect();
              onConfigurationComplete?.();
            }, 2000);
          }
        });
      });

      socket.on("connect_error", () => {
        setStatus("ERROR");
        setError("Failed to connect to server");
      });
    } catch {
      setStatus("ERROR");
      setError("Configuration failed");
    }
  };

  // Pairing login now runs inside the admin UI (so it reuses MFA / CAPTCHA /
  // lockout). Point the QR + manual-activate links at the admin UI origin.
  // Falls back to the server origin if VITE_ADMIN_BASE_URL is not configured
  // (e.g. old single-origin setups where the admin UI is proxied at the root).
  const adminBaseUrl = (import.meta.env.VITE_ADMIN_BASE_URL || ipAddress || "").replace(/\/$/, "");
  const qrUrl = pairing ? `${adminBaseUrl}/dhive/pair/${pairing.pairingId}` : "";
  const activateUrl = `${adminBaseUrl}/dhive/activate`;

  if (error) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          padding: 3,
        }}
      >
        <Typography variant="h5" sx={{ color: "#ff6b6b", mb: 3 }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{
            bgcolor: "#2196F3",
            "&:hover": { bgcolor: "#1976D2" },
            px: 4,
            py: 1.5,
            borderRadius: 2,
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }
  // if (isServerDown) {
  //   return <ServerDown />;
  // }

  if (isReloading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Box
          component="img"
          src={FullRectangleLogo}
          alt="D-hive.ai"
          sx={{ width: { xs: 220, md: 320 }, maxWidth: "80%", objectFit: "contain", mb: 3 }}
        />
        <CircularProgress size={60} sx={{ color: "#2196F3" }} />
        <Typography sx={{ mt: 2, color: "#546e7a" }}>Starting your device...</Typography>
      </Box>
    );
  }

  if (!pairing) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <Box
          component="img"
          src={FullRectangleLogo}
          alt="D-hive.ai"
          sx={{ width: { xs: 220, md: 320 }, maxWidth: "80%", objectFit: "contain", mb: 3 }}
        />
        <CircularProgress size={60} sx={{ color: "#2196F3" }} />
      </Box>
    );
  }

  // Detect if browser zoom is less than 100% (common on TV browsers)
  // and compensate with CSS zoom to ensure consistent rendering
  const browserZoom = typeof window !== "undefined" ? window.outerWidth / window.innerWidth : 1;
  const compensateZoom = browserZoom < 0.95 ? 1 / browserZoom : 1;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        overflow: "auto",
        py: 3,
        zoom: compensateZoom,
      }}
    >
      {/* Header with Branding */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box
          component="img"
          src={FullRectangleLogo}
          alt="D-hive.ai"
          sx={{
            width: { xs: 240, md: 340 },
            maxWidth: "80%",
            objectFit: "contain",
            mb: 1,
          }}
        />
        <Typography
          variant="h5"
          sx={{
            color: "#546e7a",
            fontWeight: 500,
            fontSize: { xs: "1.2rem", md: "1.5rem" },
          }}
        >
          Pair this display
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: 2,
          pb: 4,
        }}
      >
        {status === "REGENERATING" && (
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 3,
              p: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              textAlign: "center",
              maxWidth: 500,
            }}
          >
            <CircularProgress size={60} sx={{ color: "#2196F3", mb: 3 }} />
            <Typography variant="h5" sx={{ color: "#37474f", mb: 2, fontWeight: 600 }}>
              QR Code Expired
            </Typography>
            <Typography sx={{ color: "#78909c" }}>Generating a new QR code...</Typography>
          </Box>
        )}

        {status === "WAITING" && !isExpired && (
          <Box sx={{ width: "100%", maxWidth: 1000 }}>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                justifyContent: "center",
                flexWrap: "wrap",
                alignItems: "stretch",
              }}
            >
              {/* QR Code */}
              <Box
                sx={{
                  flex: "1 1 auto",
                  minWidth: 280,
                  maxWidth: 450,
                  bgcolor: "white",
                  borderRadius: 3,
                  p: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#37474f",
                      lineHeight: 1.4,
                    }}
                  >
                    Scan QR code with your phone or visit the URL below
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    my: 2,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "white",
                      padding: 2,
                      borderRadius: 2,
                      border: "2px solid #e0e0e0",
                    }}
                  >
                    <QRCodeSVG value={qrUrl} size={180} level="H" />
                  </Box>
                </Box>
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "11px", color: "#90a4ae", mb: 0.5 }}>
                    Can't scan? Visit on your phone:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#2196F3",
                      wordBreak: "break-all",
                      fontFamily: "monospace",
                    }}
                  >
                    {activateUrl}
                  </Typography>
                </Box>
              </Box>

              {/* Pairing Code */}
              <Box
                sx={{
                  flex: "1 1 auto",
                  minWidth: 280,
                  maxWidth: 450,
                  bgcolor: "white",
                  borderRadius: 3,
                  p: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#37474f",
                      lineHeight: 1.4,
                    }}
                  >
                    Enter this code on the admin panel
                  </Typography>
                </Box>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                    padding: 3,
                    borderRadius: 2,
                    my: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "3rem",
                      fontWeight: "bold",
                      letterSpacing: "0.2em",
                      background: "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontFamily: "monospace",
                    }}
                  >
                    {pairing.pairingCode}
                  </Typography>
                </Box>
                {secondsLeft != null && (
                  <Typography
                    sx={{
                      textAlign: "center",
                      mt: 1,
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      fontFamily: "monospace",
                      color: secondsLeft <= 30 ? "#e53935" : "#546e7a",
                    }}
                  >
                    Code expires in {formatCountdown(secondsLeft)}
                  </Typography>
                )}
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <CircularProgress size={24} sx={{ color: "#2196F3" }} />
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: "0.9rem",
                      color: "#78909c",
                    }}
                  >
                    Waiting for configuration...
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {status === "COMPLETED" && configData && (
          <Box
            sx={{
              textAlign: "center",
              bgcolor: "white",
              borderRadius: 3,
              p: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              maxWidth: 500,
            }}
          >
            <Typography variant="h4" sx={{ color: "#4caf50", mb: 2, fontSize: "3rem" }}>
              ✓
            </Typography>
            <Typography variant="h5" sx={{ color: "#37474f", mb: 2, fontWeight: 600 }}>
              Configuration Received!
            </Typography>
            <Typography sx={{ color: "#78909c", fontSize: "1rem" }}>
              Device Name: <strong>{configData.deviceName}</strong>
            </Typography>
            <Typography sx={{ color: "#78909c", mt: 1, fontSize: "1rem" }}>
              Registering device with server...
            </Typography>
            <CircularProgress size={40} sx={{ mt: 3, color: "#2196F3" }} />
          </Box>
        )}

        {status === "ERROR" && (
          <Box
            sx={{
              textAlign: "center",
              bgcolor: "white",
              borderRadius: 3,
              p: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              maxWidth: 500,
            }}
          >
            <Typography variant="h4" sx={{ color: "#ff6b6b", mb: 2, fontSize: "3rem" }}>
              ✕
            </Typography>
            <Typography variant="h5" sx={{ color: "#37474f", mb: 2, fontWeight: 600 }}>
              Configuration Failed
            </Typography>
            <Typography sx={{ color: "#78909c", fontSize: "1rem" }}>{error}</Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                mt: 3,
                px: 4,
                py: 1.5,
                background: "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
                fontSize: "1rem",
                textTransform: "none",
                borderRadius: 2,
                "&:hover": {
                  background: "linear-gradient(135deg, #1976D2 0%, #0d47a1 100%)",
                },
              }}
            >
              Try Again
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PairDevice;
